import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, MapPin, Clock, Check, Phone, MessageSquare, Briefcase } from 'lucide-react';
import { getHost } from '../lib/site';
import { TELEGRAM_CHANNEL, TELEGRAM_HANDLE } from '../lib/socials';

const OFFICES = [
  {
    city: 'Dubai — HQ',
    country: 'United Arab Emirates',
    addr: 'DIFC, Gate Village 4, Level 6',
    tz: 'GMT+4 · 09:00–18:00',
    phone: '+971 4 580 2847',
    img: '🏙️',
  },
  {
    city: 'Singapore',
    country: 'Singapore',
    addr: '1 Raffles Quay, #24-03 North Tower',
    tz: 'GMT+8 · 09:00–18:00',
    phone: '+65 6911 4062',
    img: '🌆',
  },
  {
    city: 'Lisbon',
    country: 'Portugal',
    addr: 'Av. da Liberdade 110, 4º',
    tz: 'GMT+0 · 09:00–18:00',
    phone: '+351 21 940 6130',
    img: '🌅',
  },
];

export default function Contact() {
  const host = getHost();
  const [form, setForm] = useState({ name: '', email: '', topic: 'support', message: '' });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 3500);
    setForm({ name: '', email: '', topic: 'support', message: '' });
  };

  return (
    <div className="pt-12 sm:pt-16 pb-16 sm:pb-20 container-x" data-testid="contact-page">
      <section className="text-center py-10">
        <span className="chip"><Mail className="w-3 h-3" /> Contact</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">
          Let&apos;s <span className="gradient-text">talk</span>
        </h1>
        <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
          Questions, bug reports, partnership ideas — pick your channel. We usually reply within a few hours.
        </p>
      </section>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 mt-6">
        <div className="space-y-4">
          {[
            { Icon: Send,         t: 'Telegram support', d: 'Fastest path for Pro & Whale. Avg reply: 12 minutes.', cta: `Open ${TELEGRAM_HANDLE}`, href: TELEGRAM_CHANNEL },
            { Icon: Mail,         t: 'Email us',         d: `team@${host} · replies within 24h.`,                   cta: 'Send email',         href: `mailto:team@${host}` },
            { Icon: Phone,        t: 'Phone (Whale)',    d: 'Whale plan only — direct line, 24/5.',                 cta: '+971 4 580 2847',    href: 'tel:+97145802847' },
            { Icon: Briefcase,    t: 'Partnerships',     d: 'Funds, OTC desks, licensing, media.',                  cta: `bd@${host}`,         href: `mailto:bd@${host}` },
          ].map((c) => (
            <a key={c.t} href={c.href} className="glass-card glass-card-hover p-5 flex items-start gap-4 group">
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
                <c.Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display font-bold text-base">{c.t}</h3>
                <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{c.d}</p>
                <span className="text-[hsl(var(--primary))] text-sm font-semibold mt-2 inline-flex items-center gap-1 group-hover:underline">{c.cta} →</span>
              </div>
            </a>
          ))}
          <div className="glass-card p-5 flex items-start gap-3">
            <Clock className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
            <div>
              <div className="text-xs text-muted-foreground">Support hours</div>
              <div className="font-semibold text-sm">24/7 online · phone 24/5</div>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-5 sm:p-7"
          data-testid="contact-form"
        >
          <h3 className="font-display font-bold text-xl">Send us a message</h3>
          <p className="text-sm text-muted-foreground mt-1">Fill the form and we will route it to the right team.</p>

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <label className="block">
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Name</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 px-3 py-2.5 rounded-lg bg-background/60 border border-border focus:border-[hsl(var(--primary))] focus:outline-none text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Email</span>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full mt-1 px-3 py-2.5 rounded-lg bg-background/60 border border-border focus:border-[hsl(var(--primary))] focus:outline-none text-sm" />
            </label>
          </div>
          <label className="block mt-4">
            <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Topic</span>
            <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 rounded-lg bg-background/60 border border-border focus:border-[hsl(var(--primary))] focus:outline-none text-sm">
              <option value="support">Support</option>
              <option value="sales">Sales / Whale plan</option>
              <option value="partnership">Partnership</option>
              <option value="press">Press</option>
              <option value="bug">Bug report</option>
            </select>
          </label>
          <label className="block mt-4">
            <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Message</span>
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 rounded-lg bg-background/60 border border-border focus:border-[hsl(var(--primary))] focus:outline-none text-sm resize-none" />
          </label>
          <button type="submit" className="btn-primary mt-5" data-testid="contact-submit">
            {sent ? <><Check className="w-4 h-4" /> Sent!</> : <><Send className="w-4 h-4" /> Send message</>}
          </button>
        </motion.form>
      </div>

      <section className="mt-16" data-testid="offices-section">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <span className="chip"><MapPin className="w-3 h-3" /> Offices</span>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-3">
              Three time zones, one team.
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-muted-foreground">
            <MessageSquare className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
            Always someone online.
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {OFFICES.map((o, i) => (
            <motion.div
              key={o.city}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              className="glass-card glass-card-hover p-6"
            >
              <div className="text-4xl">{o.img}</div>
              <div className="font-display font-bold text-lg mt-3">{o.city}</div>
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-0.5">{o.country}</div>
              <div className="text-sm text-foreground/90 mt-4 leading-relaxed">{o.addr}</div>
              <div className="text-xs text-muted-foreground mt-1">{o.tz}</div>
              <a href={`tel:${o.phone.replace(/\s/g, '')}`} className="text-sm text-[hsl(var(--primary))] font-semibold mt-3 inline-flex items-center gap-2 hover:underline">
                <Phone className="w-3.5 h-3.5" /> {o.phone}
              </a>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
